import React, { useEffect, useState } from "react";
import WorkflowCreateEditDetails from "./WorkflowCreateEditDetails";
import { WorkflowListData } from "../WorkflowConfigurations";
import WorkflowBuilderPage from "./WorkflowBuilder/WorkflowBuilderPage";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSubmitResponse,
  setSubmitResponseErrors,
} from "reducers/submitResponse";
import WorkflowCreateEditSummary from "./WorkflowCreateEditSummary";
import { endpoints } from "config";
import {
  clearWorkflowFormsDetailsData,
  setWorkflowFormsDetailsData,
} from "reducers/workflows";
import api from "api";
import { parseSubmitErrors } from "helpers/submitForm";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";

const MAX_PAGES = 3;

export enum Pages {
  "DETAILS" = 1,
  "BUILDER" = 2,
  "SUMMARY" = 3,
}

type WorkflowCreateEditProps = {
  actionType: "create" | "edit" | "view";
  selectedData: WorkflowListData[];
  onDone(): void;
  idFromRowOption: React.ReactText | null;
  submitButtonText?: string;
  hideActionButton?: boolean;
};

const WorkflowCreateEdit: React.FC<WorkflowCreateEditProps> = ({
  actionType,
  selectedData,
  onDone,
  idFromRowOption,
  submitButtonText,
  hideActionButton,
}) => {
  const [currentPage, setCurrentPage] = useState<Pages>(Pages.DETAILS);
  // const [currentPage, setCurrentPage] = useState<Pages>(Pages.BUILDER); // CHANGE BACK
  // const [currentPage, setCurrentPage] = useState<Pages>(Pages.SUMMARY);
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const [redirectedToPage, setRedirectedToPage] = useState<Pages | null>(null);

  useEffect(() => {
    if (
      selectedData &&
      Array.isArray(selectedData) &&
      selectedData.length === 1 &&
      selectedData[0] &&
      typeof selectedData === "object" &&
      selectedData[0].hasOwnProperty("workflows__ID")
    ) {
      setWorkflowId(selectedData[0].workflows__ID);
    } else if (idFromRowOption && typeof idFromRowOption === "number") {
      setWorkflowId(idFromRowOption);
    }
  }, [selectedData, idFromRowOption]);

  const nextPage = () => {
    if (currentPage < MAX_PAGES) {
      setRedirectedToPage(null);
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setRedirectedToPage(null);
      setCurrentPage(currentPage - 1);
    }
  };

  const redirectToDetailsPage = () => {
    setRedirectedToPage(Pages.DETAILS);
    setCurrentPage(Pages.DETAILS);
  };
  const redirectToBuilderPage = () => {
    setRedirectedToPage(Pages.BUILDER);
    setCurrentPage(Pages.BUILDER);
  };
  const redirectToSummaryPage = () => {
    setRedirectedToPage(Pages.SUMMARY);
    setCurrentPage(Pages.SUMMARY);
  };

  const submitResponse = useSelector(selectSubmitResponse);

  useEffect(() => {
    if (
      submitResponse.submitResponse &&
      submitResponse.submitResponse.id &&
      typeof submitResponse.submitResponse.id === "number" &&
      submitResponse.submitResponseUrl &&
      (submitResponse.submitResponseUrl ===
        endpoints.workflowConfiguration.create ||
        submitResponse.submitResponseUrl ===
          endpoints.workflowConfiguration.edit) &&
      actionType === "create"
    ) {
      setWorkflowId(submitResponse.submitResponse.id);
    }
  }, [submitResponse]);

  const dispatch = useDispatch();

  const onValuesChangeDetails = (values: any) => {
    dispatch(setWorkflowFormsDetailsData(values));
  };

  const editAdditionalSubmit = async (values: any) => {
    try {
      const response = await api.post(
        endpoints.workflowConfiguration.edit,
        values
      );

      if (
        response.status === 200 &&
        response.data &&
        response.data.status === `1`
      ) {
        dispatch(setSubmitResponseErrors({ submitResponseErrors: null }));

        toast(
          <ToastMessage
            toastData={[
              {
                title: "Workflow updated",
                text: "Workflow updated successfully",
              },
            ]}
            className="ToastSuccess"
          />,
          {
            ...toastConfig,
          }
        );

        finalOnDone();
      } else {
        toast(
          <ToastMessage
            toastData={[
              {
                title: "Workflow error",
                text:
                  "Workflow details failed to update, please return to the 'Workflow Details' tab and try again",
              },
            ]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );
      }
    } catch (err) {
      parseSubmitErrors(err, dispatch);
    }
  };

  const finalOnDone = () => {
    dispatch(clearWorkflowFormsDetailsData());

    onDone();
  };

  const redirectedToDetails = redirectedToPage === Pages.DETAILS;
  const redirectedToBuilder = redirectedToPage === Pages.BUILDER;
  const redirectedToSummary = redirectedToPage === Pages.SUMMARY;
  return (
    <div className="WorkflowCreateEdit">
      {actionType === "edit" && (
        <div className="EditTabBar">
          <div
            className={`Tab ${currentPage === Pages.DETAILS ? "Active" : ""}`}
            onClick={() => setCurrentPage(Pages.DETAILS)}
          >
            <div className="Text">Workflow Details</div>
          </div>

          <div
            className={`Tab ${currentPage === Pages.BUILDER ? "Active" : ""}`}
            onClick={() => setCurrentPage(Pages.BUILDER)}
          >
            <div className="Text">Workflow Builder</div>
          </div>
        </div>
      )}

      {currentPage === Pages.DETAILS && (
        <WorkflowCreateEditDetails
          actionType={redirectedToDetails && workflowId ? "edit" : actionType}
          originalActionType={actionType}
          selectedData={selectedData}
          onDone={() => {
            if (actionType === "edit") {
              finalOnDone();
            } else {
              nextPage();
            }
          }}
          idFromRowOption={
            actionType === "create" && redirectedToDetails && workflowId
              ? workflowId
              : idFromRowOption
          }
          currentPageNumber={1}
          totalPages={MAX_PAGES}
          cancelOnDone={finalOnDone}
          submitButtonText={
            actionType === "edit" ? "Update" : "Save and continue"
          }
          // workflowId={workflowId}
          onValuesChangeDetails={
            actionType === "edit" ? onValuesChangeDetails : undefined
          }
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}

      {currentPage === Pages.BUILDER && (
        // <div>WorkflowBuilderPage yet to be implemented.{/* TODO */}</div>
        <WorkflowBuilderPage
          closePanel={finalOnDone}
          actionType={redirectedToBuilder && workflowId ? "edit" : actionType}
          // actionType={actionType}
          originalActionType={actionType}
          selectedData={selectedData}
          onDone={() => {
            if (actionType === "edit") {
              finalOnDone();
            } else {
              nextPage();
            }
          }}
          currentPageNumber={1}
          totalPages={MAX_PAGES}
          idFromRowOption={workflowId ?? idFromRowOption}
          workflowsId={workflowId ?? idFromRowOption}
          submitButtonText={
            actionType === "edit" ? "Update" : "Save and continue"
          }
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}

          // // idFromRowOption={
          // //   actionType === "create" && redirectedToBuilder && workflowId
          // //     ? workflowId
          // //     : idFromRowOption
          // // }
          // //   actionType === "create" && redirectedToBuilder && workflowId
          // //     ? workflowId
          // //     : idFromRowOption
          // // }

          // closePanel={finalOnDone}
          // workflowId={workflowId}
          // submitButtonText={
          //   actionType === "edit" ? "Update" : "Save and continue"
          // }
          // additionalOnSubmit={
          //   actionType === "edit" ? editAdditionalSubmit : undefined
          // }
        />
      )}
      {currentPage === Pages.SUMMARY && (
        <WorkflowCreateEditSummary
          actionType={actionType}
          selectedData={selectedData}
          onDone={() => {
            finalOnDone();
          }}
          idFromRowOption={idFromRowOption}
          currentPage={3}
          totalPages={MAX_PAGES}
          cancelOnDone={finalOnDone}
          redirectToDetailsPage={redirectToDetailsPage}
          redirectToBuilderPage={redirectToBuilderPage}
          workflowId={workflowId}
        />
      )}
    </div>
  );
};

export default WorkflowCreateEdit;
